import { useState, useEffect, useMemo, useCallback, ReactNode } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase/client";

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

type LinesCarried = "1-2" | "3-5" | "6+";
type Locations    = "1" | "2+";
type ArSituation   = "none" | "webar" | "competitor";

interface RetailerOption {
  id: string;
  store_name: string;
  city: string | null;
  state: string | null;
  province: string | null;
  zip_code: string | null;
  postal_code: string | null;
  pricing_locked: boolean;
  pricing_tier: string | null;
  tier: string | null;
  rate_locked_price_cents: number | null;
}

interface TerritoryMatch {
  matched: boolean;
  tier: "A" | "B" | "C";
  multiplier: number;
  region: string | null;
}

const COMPETITORS = ["Contura", "CharpstAR"];

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

function fmtUsd(n: number): string {
  return `$${Math.round(n).toLocaleString("en-US")}`;
}

function normalisePrefix(postal: string): string {
  return postal.replace(/\s+/g, "").toUpperCase().slice(0, 3);
}

/* ------------------------------------------------------------------ */
/* Small UI helpers (mirrors AdminRetailers.tsx conventions)           */
/* ------------------------------------------------------------------ */

function Card({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-5">
      {title && <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</h3>}
      {children}
    </div>
  );
}

function BreakdownRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5 text-sm">
      <span className="text-gray-400">{label}</span>
      <span className="text-gray-200">{children}</span>
    </div>
  );
}

function TierBadge({ tier }: { tier: "A" | "B" | "C" }) {
  const meta = {
    A: { label: "Tier A — Premium",  classes: "border-teal-700 bg-teal-900/30 text-teal-400" },
    B: { label: "Tier B — Standard", classes: "border-gray-700 bg-gray-800/60 text-gray-400" },
    C: { label: "Tier C — Value",    classes: "border-amber-700 bg-amber-900/30 text-amber-400" },
  }[tier];
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${meta.classes}`}>
      {meta.label}
    </span>
  );
}

const inputStyle = { background: "#111827", color: "#f9fafb" };
const inputCls   = "w-full rounded-lg border border-gray-700 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none";
const labelCls   = "mb-1.5 block text-xs font-medium text-gray-400";

/* ------------------------------------------------------------------ */
/* Main component                                                       */
/* ------------------------------------------------------------------ */

export default function AdminPricingCalculator() {
  const { toast } = useToast();

  // ── Reference data ───────────────────────────────────────────────────
  const [pricingConfig, setPricingConfig] = useState<Record<string, number> | null>(null);
  const [foundingSpots, setFoundingSpots] = useState<{ total: number; claimed: number } | null>(null);
  const [retailers,     setRetailers]     = useState<RetailerOption[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [loadError,     setLoadError]     = useState<string | null>(null);

  // ── Inputs ────────────────────────────────────────────────────────────
  const [postalCode,     setPostalCode]     = useState("");
  const [territoryMatch, setTerritoryMatch] = useState<TerritoryMatch | null>(null);
  const [linesCarried,   setLinesCarried]   = useState<LinesCarried>("1-2");
  const [locations,      setLocations]      = useState<Locations>("1");
  const [arSituation,    setArSituation]    = useState<ArSituation>("none");
  const [competitorName, setCompetitorName] = useState("");

  // ── Retailer picker / lock ───────────────────────────────────────────
  const [selectedRetailerId, setSelectedRetailerId] = useState<string>("");
  const [lockingStandard, setLockingStandard] = useState(false);
  const [lockingFounding, setLockingFounding] = useState(false);

  const selectedRetailer = useMemo(
    () => retailers.find((r) => r.id === selectedRetailerId) ?? null,
    [retailers, selectedRetailerId],
  );

  // ── Load reference data ──────────────────────────────────────────────
  const loadReferenceData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [configRes, spotsRes, retailersRes] = await Promise.all([
        supabase.from("pricing_config").select("key, value"),
        supabase.from("founding_spots").select("total, claimed").eq("id", 1).single(),
        supabase
          .from("retailers")
          .select(
            "id, store_name, city, state, province, zip_code, postal_code, " +
            "pricing_locked, pricing_tier, tier, rate_locked_price_cents",
          )
          .order("store_name", { ascending: true }),
      ]);

      if (configRes.error) throw configRes.error;
      const cfg: Record<string, number> = {};
      for (const row of configRes.data ?? []) cfg[row.key as string] = row.value as number;
      setPricingConfig(cfg);

      if (spotsRes.error) throw spotsRes.error;
      setFoundingSpots(spotsRes.data ? { total: spotsRes.data.total, claimed: spotsRes.data.claimed } : null);

      if (retailersRes.error) throw retailersRes.error;
      setRetailers((retailersRes.data as unknown as RetailerOption[]) ?? []);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load pricing reference data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReferenceData(); }, [loadReferenceData]);

  // ── Territory resolution (debounced) ─────────────────────────────────
  useEffect(() => {
    const prefix = normalisePrefix(postalCode);
    if (prefix.length < 3 || !pricingConfig) {
      setTerritoryMatch(null);
      return;
    }
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from("territory_pricing_tiers")
        .select("tier, price_multiplier, region")
        .eq("postal_prefix", prefix)
        .maybeSingle();

      if (data) {
        setTerritoryMatch({
          matched:    true,
          tier:       data.tier as "A" | "B" | "C",
          multiplier: data.price_multiplier as number,
          region:     data.region as string | null,
        });
      } else {
        setTerritoryMatch({
          matched:    false,
          tier:       "B",
          multiplier: pricingConfig.tier_b_multiplier ?? 1.0,
          region:     null,
        });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [postalCode, pricingConfig]);

  // ── Standard tier computation ─────────────────────────────────────────
  const standardQuote = useMemo(() => {
    if (!pricingConfig || locations === "2+") return null;

    const base   = pricingConfig.standard_annual_base ?? 3600;
    const terr   = territoryMatch ?? { matched: false, tier: "B" as const, multiplier: pricingConfig.tier_b_multiplier ?? 1.0, region: null };
    const afterTerritory = base * terr.multiplier;

    const competitorApplies = arSituation === "competitor";
    const competitorMultiplier = competitorApplies ? (pricingConfig.competitor_displacement_multiplier ?? 0.95) : 1;
    const anchor = afterTerritory * competitorMultiplier;

    return {
      base,
      territoryTier: terr.tier,
      territoryMatched: terr.matched,
      territoryRegion: terr.region,
      territoryMultiplier: terr.multiplier,
      competitorApplies,
      competitorMultiplier,
      anchor,
      low:  anchor * 0.95,
      high: anchor * 1.05,
    };
  }, [pricingConfig, territoryMatch, locations, arSituation]);

  const isGrowth = locations === "2+";
  const foundingSoldOut = !!foundingSpots && foundingSpots.claimed >= foundingSpots.total;

  // ── Lock actions ──────────────────────────────────────────────────────
  async function lockStandardQuote() {
    if (!selectedRetailer || !standardQuote || selectedRetailer.pricing_locked) return;
    if (!window.confirm(
      `Lock Standard quote of ${fmtUsd(standardQuote.anchor)}/yr (Tier ${standardQuote.territoryTier}) ` +
      `to ${selectedRetailer.store_name}? This cannot be changed once locked.`,
    )) return;

    setLockingStandard(true);
    try {
      const { data, error } = await supabase
        .from("retailers")
        .update({
          pricing_tier:            standardQuote.territoryTier,
          tier:                    "standard",
          rate_locked_price_cents: Math.round(standardQuote.anchor * 100),
          pricing_locked:          true,
          pricing_locked_at:       new Date().toISOString(),
        })
        .eq("id", selectedRetailer.id)
        .select("id");
      if (error) throw error;
      // PostgREST returns 200/204 with no error when RLS (or a stale/wrong id)
      // matches zero rows — without .select() + this check, that silently
      // looks identical to success.
      if (!data || data.length === 0) {
        throw new Error("Update matched no rows — check permissions or that the retailer still exists.");
      }
      toast({ title: "Quote locked", description: `${selectedRetailer.store_name} locked at ${fmtUsd(standardQuote.anchor)}/yr` });
      await loadReferenceData();
    } catch (err) {
      toast({ title: "Lock failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setLockingStandard(false);
    }
  }

  async function lockFoundingQuote() {
    if (!selectedRetailer || selectedRetailer.pricing_locked || foundingSoldOut) return;
    if (!window.confirm(`Lock Founding rate ($2,400/yr flat) to ${selectedRetailer.store_name}? This cannot be changed once locked.`)) return;

    setLockingFounding(true);
    try {
      const { data, error } = await supabase
        .from("retailers")
        .update({
          pricing_tier:            null,
          tier:                    "founding",
          rate_locked_price_cents: 240000,
          pricing_locked:          true,
          pricing_locked_at:       new Date().toISOString(),
        })
        .eq("id", selectedRetailer.id)
        .select("id");
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error("Update matched no rows — check permissions or that the retailer still exists.");
      }
      toast({ title: "Quote locked", description: `${selectedRetailer.store_name} locked at Founding rate` });
      await loadReferenceData();
    } catch (err) {
      toast({ title: "Lock failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setLockingFounding(false);
    }
  }

  /* ── Render ─────────────────────────────────────────────────────────── */
  if (loading) {
    return <div className="rounded-xl border border-gray-800 py-16 text-center text-sm text-gray-500">Loading pricing data…</div>;
  }

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div>
        <h1 className="text-2xl font-semibold">Pricing Calculator</h1>
        <p className="mt-0.5 text-sm text-gray-400">
          Internal sales tool — territory + profile-based Standard tier quoting. Not retailer-facing.
        </p>
      </div>

      {loadError && (
        <div className="rounded-lg border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-400">
          {loadError}
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* ── Left column: inputs ── */}
        <div className="space-y-5">
          <Card title="Retailer (optional)">
            <select
              value={selectedRetailerId}
              onChange={(e) => setSelectedRetailerId(e.target.value)}
              style={inputStyle}
              className={inputCls}
            >
              <option value="">Prospect only — no retailer record yet</option>
              {retailers.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.store_name}{r.city ? ` — ${r.city}, ${r.state ?? r.province ?? ""}` : ""}
                  {r.pricing_locked ? " (locked)" : ""}
                </option>
              ))}
            </select>

            {selectedRetailer?.pricing_locked && (
              <div className="mt-3 rounded-lg border border-green-800 bg-green-950/30 px-3 py-2 text-xs text-green-400">
                Already locked — {selectedRetailer.tier === "founding" ? "Founding, $2,400/yr flat" :
                  `${selectedRetailer.tier ?? "Standard"}${selectedRetailer.pricing_tier ? `, Tier ${selectedRetailer.pricing_tier}` : ""}, ` +
                  `${selectedRetailer.rate_locked_price_cents != null ? fmtUsd(selectedRetailer.rate_locked_price_cents / 100) + "/yr" : ""}`}
                {" "}— pricing cannot be changed for this retailer.
              </div>
            )}
          </Card>

          <Card title="Territory">
            <label className={labelCls}>Postal code / ZIP</label>
            <input
              type="text"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              placeholder="e.g. M5V 2T6 or 90210"
              style={inputStyle}
              className={inputCls}
            />
            {territoryMatch && (
              <div className="mt-3 flex items-center gap-2">
                <TierBadge tier={territoryMatch.tier} />
                <span className="text-xs text-gray-500">
                  {territoryMatch.matched
                    ? (territoryMatch.region ?? "Matched territory") + ` · ×${territoryMatch.multiplier.toFixed(2)}`
                    : "No territory data yet for this area — using standard market tier"}
                </span>
              </div>
            )}
          </Card>

          <Card title="Retailer profile (self-reported, live on call)">
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Manufacturer lines / brands carried</label>
                <select value={linesCarried} onChange={(e) => setLinesCarried(e.target.value as LinesCarried)} style={inputStyle} className={inputCls}>
                  <option value="1-2">1–2</option>
                  <option value="3-5">3–5</option>
                  <option value="6+">6+</option>
                </select>
                {/* Context only today — no price impact. Once this retailer has rows in
                    retailer_products, lines carried can be computed via a join through
                    products.manufacturer_id instead of self-reported. */}
                <p className="mt-1 text-xs text-gray-600">No price impact — context for the rep.</p>
              </div>

              <div>
                <label className={labelCls}>Number of locations</label>
                <select value={locations} onChange={(e) => setLocations(e.target.value as Locations)} style={inputStyle} className={inputCls}>
                  <option value="1">1</option>
                  <option value="2+">2+</option>
                </select>
                {isGrowth && <p className="mt-1 text-xs text-amber-400">2+ locations routes to Growth tier below.</p>}
              </div>

              <div>
                <label className={labelCls}>Existing AR / 3D tool in place</label>
                <select
                  value={arSituation}
                  onChange={(e) => { setArSituation(e.target.value as ArSituation); if (e.target.value !== "competitor") setCompetitorName(""); }}
                  style={inputStyle}
                  className={inputCls}
                >
                  <option value="none">None (greenfield)</option>
                  <option value="webar">WebAR (generic, browser-based)</option>
                  <option value="competitor">Named native-AR competitor</option>
                </select>

                {arSituation === "webar" && (
                  <div className="mt-2 rounded-lg border border-teal-800 bg-teal-950/30 px-3 py-2 text-xs text-teal-400">
                    Talking point: WebAR can't do pre-AR configurator, clearance overlays, or deep-link
                    attribution — lead with that, not price. No discount applied.
                  </div>
                )}

                {arSituation === "competitor" && (
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {COMPETITORS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setCompetitorName(c)}
                          className={`rounded-full border px-2.5 py-1 text-xs transition ${
                            competitorName === c
                              ? "border-teal-600 bg-teal-950/40 text-teal-400"
                              : "border-gray-700 text-gray-400 hover:border-gray-500"
                          }`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={competitorName}
                      onChange={(e) => setCompetitorName(e.target.value)}
                      placeholder="Competitor name"
                      style={inputStyle}
                      className={inputCls}
                    />
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* ── Right column: output ── */}
        <div className="space-y-5">
          {isGrowth ? (
            <Card>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center rounded-full border border-amber-700 bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-400">
                  Growth tier
                </span>
              </div>
              <p className="text-lg font-semibold text-white">Custom quote required</p>
              <p className="mt-2 text-sm text-gray-400">
                2+ locations routes to the Growth tier. This calculator does not generate an automated
                number for multi-location networks — work out custom pricing directly with the prospect
                and finalize their plan manually once terms are set.
              </p>
            </Card>
          ) : standardQuote && (
            <Card title="Standard tier — suggested quote">
              <div className="mb-4">
                <p className="text-3xl font-semibold text-white tabular-nums">{fmtUsd(standardQuote.anchor)}<span className="text-base font-normal text-gray-500">/yr</span></p>
                <p className="mt-1 text-sm text-gray-500 tabular-nums">
                  Range: {fmtUsd(standardQuote.low)} – {fmtUsd(standardQuote.high)}/yr
                </p>
              </div>

              <div className="border-t border-gray-800 pt-3">
                <BreakdownRow label="Standard base">{fmtUsd(standardQuote.base)}/yr</BreakdownRow>
                <BreakdownRow label={`Territory tier ${standardQuote.territoryTier} adjustment`}>
                  ×{standardQuote.territoryMultiplier.toFixed(2)}
                </BreakdownRow>
                {standardQuote.competitorApplies && (
                  <BreakdownRow label="Competitive displacement adjustment">
                    <span className="text-teal-400">
                      {Math.round((standardQuote.competitorMultiplier - 1) * 100)}%
                    </span>
                  </BreakdownRow>
                )}
              </div>

              <button
                onClick={lockStandardQuote}
                disabled={!selectedRetailer || selectedRetailer.pricing_locked || lockingStandard}
                className="mt-4 w-full rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-teal-600 disabled:opacity-40"
              >
                {lockingStandard ? "Locking…" : "Lock this Standard quote to retailer"}
              </button>
              {!selectedRetailer && (
                <p className="mt-1.5 text-xs text-gray-600">Select a retailer above to enable locking.</p>
              )}
            </Card>
          )}

          <Card title="Founding tier — flat rate (territory- and profile-blind)">
            <p className="text-3xl font-semibold text-white tabular-nums">
              {fmtUsd(pricingConfig?.founding_annual_flat ?? 2400)}<span className="text-base font-normal text-gray-500">/yr</span>
            </p>

            {foundingSpots && (
              <div className="mt-4">
                <div className="mb-1.5 flex items-center justify-between text-xs text-gray-500">
                  <span>{foundingSpots.claimed} / {foundingSpots.total} spots claimed</span>
                  <span className={foundingSoldOut ? "text-red-400" : "text-green-400"}>
                    {foundingSoldOut ? "Sold out" : `${foundingSpots.total - foundingSpots.claimed} remaining`}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-800">
                  <div
                    className="h-full rounded-full bg-teal-500 transition-all duration-500"
                    style={{ width: `${Math.min(100, (foundingSpots.claimed / foundingSpots.total) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            <button
              onClick={lockFoundingQuote}
              disabled={!selectedRetailer || selectedRetailer.pricing_locked || foundingSoldOut || lockingFounding}
              className="mt-4 w-full rounded-lg border border-teal-800 px-4 py-2 text-sm font-medium text-teal-400 transition hover:border-teal-600 hover:bg-teal-950 disabled:opacity-40"
            >
              {lockingFounding ? "Locking…" : foundingSoldOut ? "All spots claimed" : "Lock as Founding to retailer"}
            </button>
          </Card>
        </div>
      </div>
    </div>
  );
}
